import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import * as Icon from 'react-feather';

import {
  callexAiAgentChat,
  callexAiAgentChatClose
} from '../../../store/slices/tenantSlices/configurationSlice';

// One id per chat session; the bot uses it to remember earlier turns. randomUUID only
// exists on secure origins, and the portal can also be served over plain http.
const newConversationId = () =>
  window.crypto?.randomUUID?.() || `chat-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const formatTime = (timestamp) =>
  new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const CallexAgentChatModal = ({ isOpen, toggle, agent }) => {
  const dispatch = useDispatch();
  const scrollRef = useRef(null);
  // The in-flight reply, aborted when the chat is ended or the window closes.
  const requestRef = useRef(null);

  const [conversationId, setConversationId] = useState(newConversationId);
  const [openedAt, setOpenedAt] = useState(Date.now);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  // The chat exists in the Chat Report once the bot accepts a message; a rejected first
  // message (400 / 404 / 409) leaves nothing to close.
  const [isStarted, setIsStarted] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [isEnded, setIsEnded] = useState(false);

  const startNewChat = () => {
    requestRef.current?.abort();
    setConversationId(newConversationId());
    setOpenedAt(Date.now());
    setMessages([]);
    setText('');
    setIsReplying(false);
    setIsStarted(false);
    setIsEnded(false);
  };

  useEffect(() => {
    if (isOpen) startNewChat();
  }, [isOpen, agent?.id]);

  useEffect(() => () => requestRef.current?.abort(), []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    const value = text.trim();
    if (!value || isReplying || isEnding || isEnded || !agent?.id) return;

    const now = Date.now();
    const botMessageId = `${now}-bot`;
    const updateBotMessage = (update) =>
      setMessages((prev) =>
        prev.map((msg) => (msg.id === botMessageId ? { ...msg, ...update(msg) } : msg))
      );

    setText('');
    setMessages((prev) => [
      ...prev,
      { id: `${now}-user`, sender: 'user', text: value, timestamp: now },
      { id: botMessageId, sender: 'bot', text: '', timestamp: now }
    ]);
    setIsReplying(true);

    const request = dispatch(
      callexAiAgentChat({
        id: agent.id,
        text: value,
        conversation_id: conversationId,
        onDelta: (delta) => {
          setIsStarted(true);
          updateBotMessage((msg) => ({ text: msg.text + delta }));
        }
      })
    );
    requestRef.current = request;
    try {
      const { reply } = await request.unwrap();
      setIsStarted(true);
      if (!reply) updateBotMessage(() => ({ error: 'No reply from the bot. Please try again.' }));
    } catch (error) {
      if (error?.name === 'AbortError') {
        updateBotMessage((msg) => (msg.text ? {} : { error: 'Reply stopped.' }));
        return;
      }
      console.log('error', error);
      updateBotMessage(() => ({ error: error?.message || 'Chat failed. Please try again.' }));
    } finally {
      if (requestRef.current === request) {
        requestRef.current = null;
        setIsReplying(false);
      }
    }
  };

  const endChat = async () => {
    requestRef.current?.abort();
    setIsEnding(true);
    try {
      await dispatch(
        callexAiAgentChatClose({ id: agent?.id, conversation_id: conversationId })
      ).unwrap();
      setIsEnded(true);
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsEnding(false);
    }
  };

  // Closing the window ends the chat too. Not awaited so the window closes right away;
  // a chat this call misses is still closed by the bot for inactivity.
  const handleClose = () => {
    requestRef.current?.abort();
    if (isStarted && !isEnded && agent?.id) {
      dispatch(callexAiAgentChatClose({ id: agent.id, conversation_id: conversationId }));
    }
    toggle();
  };

  const renderMessage = (msg) => {
    const isBot = msg.sender === 'bot';
    return (
      <div key={msg.id} className={`chat-row ${isBot ? 'assistant' : 'user'}`}>
        {isBot && (
          <div className="avatar assistant">
            <Icon.Cpu size={16} />
          </div>
        )}
        <div className="message-group">
          <div className="chat-bubble" style={{ whiteSpace: 'pre-wrap' }}>
            {msg.text ||
              (isBot && !msg.error ? <Icon.Loader size={14} className="spin text-muted" /> : null)}
            {msg.error && <div className="text-danger small">{msg.error}</div>}
          </div>
          <div className="chat-time">{formatTime(msg.timestamp)}</div>
        </div>
        {!isBot && (
          <div className="avatar user">
            <Icon.User size={16} />
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg" backdrop="static">
      <ModalHeader toggle={handleClose} className="py-2">
        <div className="fs-5">{agent?.name ? `Chat — ${agent.name}` : 'AI Agent Chat'}</div>
      </ModalHeader>

      <ModalBody className="call-details-modal p-0">
        <div ref={scrollRef} style={{ height: 'calc(100vh - 300px)', overflowY: 'auto' }}>
          <div className="chat-wrapper" style={{ minHeight: '100%' }}>
            {/* The bot stores its welcome message as the opening reply on the first turn. */}
            {agent?.welcome_message &&
              renderMessage({
                id: 'welcome',
                sender: 'bot',
                text: agent.welcome_message,
                timestamp: openedAt
              })}
            {messages.length === 0 && !agent?.welcome_message ? (
              <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted opacity-50 py-5">
                <Icon.MessageSquare size={48} className="mb-2" />
                <p>Send a message to start chatting</p>
              </div>
            ) : (
              messages.map(renderMessage)
            )}
          </div>
        </div>
      </ModalBody>

      <ModalFooter className="d-block">
        {isEnded ? (
          <div className="d-flex justify-content-between align-items-center gap-2">
            <span className="text-muted small">Chat ended. You can find it in AI Chat Report.</span>
            <Button color="primary" className="px-4" onClick={startNewChat}>
              New Chat
            </Button>
          </div>
        ) : (
          <form
            className="d-flex align-items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage();
            }}>
            <Input
              value={text}
              placeholder="Type a message"
              onChange={(e) => setText(e.target.value)}
              disabled={isEnding}
              autoFocus
            />
            <Button
              type="submit"
              color="primary"
              className="px-3"
              title="Send"
              disabled={!text.trim() || isReplying || isEnding}>
              <Icon.Send size={16} />
            </Button>
            <Button
              outline
              color="danger"
              className="text-nowrap"
              onClick={endChat}
              disabled={!isStarted || isEnding}>
              {isEnding ? 'Ending...' : 'End Chat'}
            </Button>
          </form>
        )}
      </ModalFooter>
    </Modal>
  );
};

CallexAgentChatModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  agent: PropTypes.object
};

export default CallexAgentChatModal;
